// PDP add-to-cart button — state machine, wipe, and text roll.
//
// "Select a size" -> "Add to cart" used to be a bare textContent swap, which
// made the most important state change on the page invisible. Here the button
// becomes a visibly different object: a filled surface wipes in from the left
// underneath a label that rolls over.
//
// This REPLACES the four places that previously wrote the button's text
// (an updateButtonState helper, a duplicated block in the variant-picker
// change handler, the variant:update handler, and the ajax submit handler's
// originalText capture/restore). One of those branched on reading the text
// back out, which a roll would have broken outright — with two nodes in the
// container mid-transition there is no single "current" string to read.
// Callers now say what state the button is in and never touch its contents.

const COPY = {
  idle: 'Select a size',
  armed: 'Add to cart',
  soldout: 'Sold out',
  busy: 'Adding…',
  done: 'Added',
  error: 'Try again',
};

// States where the fill is drawn. The wipe animates only when crossing the
// idle boundary — re-wiping on every text change would turn the one moment
// worth noticing into noise.
const FILLED = new Set(['armed', 'soldout', 'busy', 'done', 'error']);

// A response faster than perception makes the confirmation invisible.
const DONE_HOLD = 1500;
const ERROR_HOLD = 2000;

class ShabibiCta extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.shabibi-btn-add');
    this.fill = this.querySelector('.shabibi-cta__fill');
    this.labelRoll = this.querySelector('[data-cta-label]');
    this.priceRoll = this.querySelector('[data-cta-price]');
    if (!this.button) return;

    this.state = this.dataset.state || 'idle';
    this.revertTimer = null;

    // Merchant-editable labels, read once from this element's own data
    // attributes (sourced from the section's cta_label_* settings) rather
    // than re-read on every state change. setState() stays the only writer
    // of the button text; this only changes where its defaults come from.
    // done/error stay hardcoded — transient technical states, not copy a
    // merchant edits.
    this.copy = {
      ...COPY,
      idle: this.dataset.labelIdle || COPY.idle,
      armed: this.dataset.labelArmed || COPY.armed,
      soldout: this.dataset.labelSoldout || COPY.soldout,
    };

    // will-change only for the duration of the transition; declaring it
    // permanently promotes a layer for the life of the page.
    if (this.fill) {
      this.fill.addEventListener('transitionstart', () => {
        this.fill.style.willChange = 'clip-path';
      });
      this.fill.addEventListener('transitionend', () => {
        this.fill.style.willChange = '';
      });
    }
  }

  /**
   * @param {string} next - One of the keys in COPY.
   * @param {{price?: string, label?: string}} [options]
   */
  setState(next, options = {}) {
    if (!this.button || !this.copy[next]) return;

    if (this.revertTimer) {
      clearTimeout(this.revertTimer);
      this.revertTimer = null;
    }

    const previous = this.state;
    this.state = next;
    this.dataset.state = next;

    // aria-disabled, never the disabled attribute: a disabled button drops
    // out of the tab order and announces nothing, so a keyboard user gets a
    // dead control with no explanation. This one stays reachable and, when
    // pressed in idle, points at the blocker instead.
    this.button.setAttribute('aria-disabled', next === 'idle' ? 'true' : 'false');

    this.roll(this.labelRoll, options.label || this.copy[next]);
    if (options.price) this.roll(this.priceRoll, options.price);

    // Terminal feedback states fall back on their own.
    if (next === 'done') {
      this.revertTimer = setTimeout(() => this.setState('armed'), DONE_HOLD);
    } else if (next === 'error') {
      this.revertTimer = setTimeout(() => this.setState('armed'), ERROR_HOLD);
    }

    return { previous, next };
  }

  /**
   * Swaps the text in a roll container: the outgoing node lifts away while
   * the incoming one arrives from below. Consistent direction is what makes
   * it read as one mechanism rather than two unrelated fades.
   *
   * Deviation from the original spec, which absolutely positions BOTH nodes:
   * the button is a space-between flex row, so two absolute children collapse
   * the container to zero width and the label and price pile up. Keeping the
   * incoming node in flow and lifting only the outgoing one preserves the
   * width, and the container's fixed height already prevents the height jump
   * the spec was guarding against.
   */
  roll(container, text) {
    if (!container) return;

    const current = container.querySelector('.shabibi-cta__now');
    if (current && current.textContent === text) return;

    // If a third change lands mid-roll, drop anything already leaving rather
    // than queueing — never more than two nodes in the container.
    container.querySelectorAll('.shabibi-cta__leaving').forEach((node) => node.remove());

    if (current) {
      current.classList.remove('shabibi-cta__now');
      current.classList.add('shabibi-cta__leaving');
      current.addEventListener('transitionend', () => current.remove(), { once: true });
      // transitionend never fires if the element is display:none or the
      // transition is suppressed by reduced motion, so guarantee cleanup.
      setTimeout(() => current.remove(), 400);
    }

    const incoming = document.createElement('span');
    incoming.className = 'shabibi-cta__now shabibi-cta__entering';
    incoming.textContent = text;
    container.appendChild(incoming);

    // Force a frame so the entering position is the transition's start
    // value rather than being collapsed into the final one.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => incoming.classList.remove('shabibi-cta__entering'));
    });
  }

  get isFilled() {
    return FILLED.has(this.state);
  }
}

if (!customElements.get('shabibi-cta')) {
  customElements.define('shabibi-cta', ShabibiCta);
}
